import { useAuth } from "@clerk/clerk-react";
import { LoadingButton } from "@mui/lab";
import {
  Box,
  Button,
  Divider,
  FormControlLabel,
  FormHelperText,
  Grid,
  IconButton,
  Link,
  ListItemText,
  MenuItem,
  MenuList,
  Switch,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { grey } from "@mui/material/colors";
import { AlertContext } from "contexts/Alert";
import { DiscoverViewContext } from "contexts/DiscoverView";
import { OrganizationUserContext } from "contexts/Organization";
import lodash from "lodash";
import {
  Dispatch,
  SetStateAction,
  useContext,
  useEffect,
  useState,
} from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  BentoSearchFilter,
  OrganizationProfile,
  VaCollabPreference,
} from "schemas/dashboard";
import { routes } from "schemas/routes";

import ConfirmDialog from "components/Dialogs/ConfirmDialog";
import {
  BulkSendSettingsStatus,
  OrganizationBulkSendSettings,
} from "features/Influencer/Discover/Results/DiscoverFilter/AutomationVA/UpcomingPitchScheduleDialog/schema";
import { fetcherAuth } from "utils/api";
import { trackEvent } from "utils/tracking";

import BrandPreferencesSettings from "./BrandPreferences";
import OnboardingSettings from "./OnboardingSettings";

const styles = {
  backIcon: {
    border: `1.5px solid ${grey[500]}`,
    fontSize: "0.8rem",
  },
  button: {
    px: 4,
    height: "40px",
  },
  cancelButton: {
    backgroundColor: "#F1F2EF",
    color: "primary.main",
  },
  selectedMenuItem: {
    pl: 1.5,
    borderLeftWidth: "3px",
    borderLeftStyle: "solid",
    borderLeftColor: "primary.main",
    color: "primary.main",
  },
  menuText: {
    fontSize: { xs: "14px", lg: "16px" },
  },
  drawer: {
    borderRight: `1px solid ${grey[300]}`,
    pt: 2,
    pb: 10,
  },
  settingContainer: {
    px: { xs: 1, sm: 4 },
    py: 4,
  },
  pauseFormControl: {
    "& .MuiFormControlLabel-label": {
      fontWeight: 600,
      fontSize: "15px",
    },
  },
};

interface IProps {
  handleClose: () => void;
  bulkSendSetting: OrganizationBulkSendSettings;
  setBulkSendSetting: Dispatch<
    SetStateAction<OrganizationBulkSendSettings | null>
  >;
}

type EditSettingBody = {
  status?: BulkSendSettingsStatus;
  isUgcCreator?: boolean;
  isInfluencer?: boolean;
  otherType?: string;
  includeBentoSearchFilterIds?: number[];
  excludeBentoSearchFilterIds?: number[];
  openToGifted?: VaCollabPreference;
  openToAffiliates?: VaCollabPreference;
  openToForms?: VaCollabPreference;
  openToSocial?: VaCollabPreference;
};

enum SelectedSetting {
  general = "general",
  onboarding = "onboarding",
  payInfo = "payInfo",
  preferences = "preferences",
  help = "help",
}

const SETTINGS_MENU = [
  {
    key: SelectedSetting.general,
    title: "General Settings",
  },
  {
    key: SelectedSetting.onboarding,
    title: "Onboarding",
  },
  {
    key: SelectedSetting.payInfo,
    title: "Brand Pay Info",
  },
  {
    key: SelectedSetting.preferences,
    title: "Brand Preferences",
  },
  {
    key: SelectedSetting.help,
    title: "Help",
  },
];

export default function GeneralSettings({
  handleClose,
  bulkSendSetting,
  setBulkSendSetting,
}: IProps) {
  const theme = useTheme();
  const { lowerWidth } = useContext(DiscoverViewContext);
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const isMobileScreen = isSmallScreen || lowerWidth;

  const [confirmCancel, setConfirmCancel] = useState<boolean>(false);
  const {
    profile,
    setProfile,
    organizationVaProfile,
    includedBentoSearchFilterIds,
    excludedBentoSearchFilterIds,
    setIncludedBentoSearchFilterIds,
    setExcludedBentoSearchFilterIds,
  } = useContext(OrganizationUserContext);
  const [selectedSetting, setSelectedSetting] = useState<SelectedSetting>(
    SelectedSetting.general,
  );
  const [localStatus, setLocalStatus] = useState<BulkSendSettingsStatus>(
    bulkSendSetting.status || BulkSendSettingsStatus.active,
  );
  const { setErrorAlert, setAlert } = useContext(AlertContext);
  const { currentOrg } = useContext(OrganizationUserContext);
  const { getToken } = useAuth();
  const [vaSearchFilters, setVaSearchFilters] = useState<BentoSearchFilter[]>(
    [],
  );
  const [isEdited, setIsEdited] = useState<boolean>(false);
  const [localProfile, setLocalProfile] = useState<OrganizationProfile | null>(
    profile,
  );
  const [
    localIncludedBentoSearchFilterIds,
    setLocalIncludedBentoSearchFilterIds,
  ] = useState<number[]>(includedBentoSearchFilterIds);
  const [
    localExcludedBentoSearchFilterIds,
    setLocalExcludedBentoSearchFilterIds,
  ] = useState<number[]>(excludedBentoSearchFilterIds);
  const [loading, setLoading] = useState<boolean>(false);
  const [warningDialog, setWarningDialog] = useState<boolean>(false);

  const fetchFilters = async () => {
    try {
      const res = await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/bento-search-filters?for_va=true`,
        "GET",
      );
      setVaSearchFilters(res.bentoSearchFilters);
    } catch (error) {
      setErrorAlert(error);
    }
  };

  useEffect(() => {
    fetchFilters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const closeSettings = () => {
    if (isEdited) {
      setConfirmCancel(true);
      return;
    }

    handleClose();
  };

  const saveChanges = async () => {
    const body: EditSettingBody = {};
    if (localStatus !== bulkSendSetting.status) {
      body.status = localStatus;
    }
    if (localProfile?.isUgcCreator !== profile?.isUgcCreator) {
      body.isUgcCreator = localProfile?.isUgcCreator;
    }
    if (localProfile?.isInfluencer !== profile?.isInfluencer) {
      body.isInfluencer = localProfile?.isInfluencer;
    }
    if (localProfile?.otherType !== profile?.otherType) {
      body.otherType = localProfile?.otherType;
    }

    if (
      !lodash.isEqual(
        localExcludedBentoSearchFilterIds,
        excludedBentoSearchFilterIds,
      )
    ) {
      body.excludeBentoSearchFilterIds = localExcludedBentoSearchFilterIds;
    }
    if (
      !lodash.isEqual(
        localIncludedBentoSearchFilterIds,
        includedBentoSearchFilterIds,
      )
    ) {
      body.includeBentoSearchFilterIds = localIncludedBentoSearchFilterIds;
    }

    if (organizationVaProfile) {
      body.openToGifted = organizationVaProfile.openToGifted;
      body.openToAffiliates = organizationVaProfile.openToAffiliates;
      body.openToForms = organizationVaProfile.openToForms;
      body.openToSocial = organizationVaProfile.openToSocial;
    }

    setLoading(true);

    try {
      await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/va-settings`,
        "PUT",
        {},
        body,
      );

      if (
        localStatus === BulkSendSettingsStatus.inactive &&
        bulkSendSetting.status === BulkSendSettingsStatus.active
      ) {
        setWarningDialog(true);
      }

      setBulkSendSetting((v) => {
        if (!v) return v;
        const copy = { ...v };
        copy.status = body.status;
        return copy;
      });
      if (localProfile) {
        setProfile(localProfile);
      }
      setIncludedBentoSearchFilterIds(localIncludedBentoSearchFilterIds);
      setExcludedBentoSearchFilterIds(localExcludedBentoSearchFilterIds);
      setAlert("Successfully saved settings", "success");
      setIsEdited(false);
    } catch (error) {
      setErrorAlert(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ p: 2, pb: { xs: 10, md: 2 } }}>
      <Box
        display="flex"
        gap={1}
        flexWrap="wrap"
        justifyContent="space-between"
        alignItems="center"
      >
        <Box display="flex" alignItems="center" gap={2}>
          <Box>
            <IconButton
              size="small"
              sx={styles.backIcon}
              onClick={closeSettings}
            >
              <Box component="span" className="fa-regular fa-arrow-left" />
            </IconButton>
          </Box>
          <Box>
            <Typography variant="h3">Settings</Typography>
          </Box>
        </Box>
        <Box display="flex" gap={1}>
          <LoadingButton
            variant="contained"
            color="primary"
            sx={[styles.button]}
            disableElevation
            disabled={!isEdited}
            onClick={() => saveChanges()}
            loading={loading}
          >
            Save All Updates
          </LoadingButton>
          <Button
            variant="contained"
            color="inherit"
            sx={[styles.button, styles.cancelButton]}
            disableElevation
            onClick={closeSettings}
          >
            Cancel
          </Button>
        </Box>
      </Box>
      <Divider sx={{ mt: 2 }} />
      <Grid container>
        {!isMobileScreen && (
          <Grid item xs="auto" lg={3} sx={styles.drawer}>
            <MenuList>
              {SETTINGS_MENU.map((settingMenu) => (
                <MenuItem
                  key={settingMenu.key}
                  onClick={() => {
                    setSelectedSetting(settingMenu.key);
                    trackEvent("VA Settings Page Changed Settings Tab", {
                      tab: settingMenu.title,
                    });
                  }}
                  sx={{ mr: { xs: 1, lg: 10 } }}
                >
                  <ListItemText
                    sx={[
                      selectedSetting === settingMenu.key &&
                        styles.selectedMenuItem,
                    ]}
                  >
                    <Typography variant="h6" sx={styles.menuText}>
                      {settingMenu.title}
                    </Typography>
                  </ListItemText>
                </MenuItem>
              ))}
            </MenuList>
          </Grid>
        )}
        <Grid item xs sx={styles.settingContainer}>
          {(selectedSetting === SelectedSetting.general || isMobileScreen) && (
            <>
              <FormControlLabel
                control={
                  <Switch
                    checked={localStatus === BulkSendSettingsStatus.inactive}
                    onChange={(e) => {
                      setLocalStatus(
                        e.target.checked
                          ? BulkSendSettingsStatus.inactive
                          : BulkSendSettingsStatus.active,
                      );
                      setIsEdited(true);
                    }}
                  />
                }
                sx={styles.pauseFormControl}
                label="Pause Brand Outreach (Account Remains Active)"
              />
              <FormHelperText sx={{ color: grey[700], mb: 4 }}>
                Once paused, your Virtual Assistant will stop sending new
                outreach messages. Scheduled follow-ups will still go out as
                planned. Your account and progress remain active — you can
                restart outreach anytime.
              </FormHelperText>
            </>
          )}
          {(selectedSetting === SelectedSetting.onboarding ||
            isMobileScreen) && (
            <OnboardingSettings
              vaSearchFilters={vaSearchFilters}
              profile={localProfile}
              setProfile={setLocalProfile}
              includedBentoSearchFilterIds={localIncludedBentoSearchFilterIds}
              excludedBentoSearchFilterIds={localExcludedBentoSearchFilterIds}
              setIncludedBentoSearchFilterIds={
                setLocalIncludedBentoSearchFilterIds
              }
              setIsEdited={setIsEdited}
              setExcludedBentoSearchFilterIds={
                setLocalExcludedBentoSearchFilterIds
              }
            />
          )}
          {(selectedSetting === SelectedSetting.payInfo || isMobileScreen) && (
            <>
              <Typography
                variant="h6"
                sx={{ fontSize: "15px", mt: 1, fontWeight: 600 }}
                gutterBottom
              >
                Want Pay Info on a Brand?
              </Typography>
              <Typography paragraph>
                Forward any conversation you're having with a brand about rates
                to <strong>negotiations@onbento.com</strong>, and Bento will
                send you (anonymous) pay data we have about the brand to help
                you negotiate the best rates!
              </Typography>
            </>
          )}
          {(selectedSetting === SelectedSetting.preferences ||
            isMobileScreen) && (
            <Box sx={{ my: 2 }}>
              {isMobileScreen && (
                <Typography
                  variant="h6"
                  sx={{ fontSize: "15px", my: 2, fontWeight: 600 }}
                  gutterBottom
                >
                  Brand Preferences
                </Typography>
              )}
              <BrandPreferencesSettings setIsEdited={setIsEdited} />
            </Box>
          )}
          {(selectedSetting === SelectedSetting.help || isMobileScreen) && (
            <>
              <Typography
                variant="h6"
                sx={{ fontSize: "15px", fontWeight: 600, mt: 1 }}
                gutterBottom
              >
                Want to stay updated?
              </Typography>
              <Typography paragraph>
                Want a heads-up when your pitches are scheduled each week?
                Download our app to stay in the loop.
              </Typography>
              <Typography paragraph>
                👉 Download our app on{" "}
                <Link
                  href="https://apps.apple.com/us/app/bento-virtual-assistant/id6738798823"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  iOS
                </Link>{" "}
                or{" "}
                <Link
                  href="https://play.google.com/store/apps/details?id=com.onbento.bento"
                  target="_blank"
                >
                  Android
                </Link>
              </Typography>
              <Typography
                variant="h6"
                sx={{ fontSize: "15px", fontWeight: 600 }}
                gutterBottom
              >
                Need any other help?
              </Typography>
              <Typography paragraph>
                You get a concierge email dedicated only to VA users. Email us
                at{" "}
                <strong>
                  <a href="mailto:va@onbento.com">va@onbento.com</a>
                </strong>{" "}
              </Typography>
            </>
          )}
        </Grid>
      </Grid>

      <ConfirmDialog
        header={`Are you sure?`}
        subheader={`You have some unsaved changes. Are you sure you want to exit and lose all progress?`}
        open={confirmCancel}
        handleClose={() => setConfirmCancel(false)}
        handleConfirm={handleClose}
        cancelText="No"
        confirmText="Yes"
      />

      <ConfirmDialog
        header={`⚠️ Warning`}
        subheader={
          <>
            <Typography paragraph>
              <strong>Outreach has been paused.</strong>
            </Typography>
            <Typography paragraph>
              Your Virtual Assistant will no longer send <em>new</em> outreach
              emails, but <strong>any emails already scheduled</strong> will
              still be delivered as planned. You can resume outreach at any
              time.
            </Typography>
            <Typography>
              If you'd like to review or delete scheduled emails, head to your{" "}
              <Link component={RouterLink} to={`/${routes.inbox}/scheduled`}>
                Scheduled Emails
              </Link>{" "}
              page.
            </Typography>
          </>
        }
        open={warningDialog}
        handleConfirm={() => setWarningDialog(false)}
        confirmText="Okay"
      />
    </Box>
  );
}
